/**
 * Component: Widget Init Pipeline
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R08, WIDGET-V1-R14, WIDGET-V1-R15,
 *               WIDGET-V1-R16, WIDGET-V1-R18
 *
 * Orchestrates the widget initialisation flow exactly as
 * specified in widget.md §4.4:
 *
 *   1. R18 Version handshake. Abort early on mismatch.
 *   2. R14 Runtime config fetch (the R08 401 retry layer
 *      in WidgetApiClient handles auth refresh).
 *   3. R16 Theme application.
 *   4. R15 Enabled-surfaces allowlist check (only when the
 *      caller requested a specific surface).
 *
 * Any failure is surfaced in two ways:
 *
 *   - The promise returned by ``runInitPipeline`` rejects
 *     with the typed error so programmatic callers can
 *     pattern-match.
 *   - If the caller supplied an ``onError`` callback, the
 *     same error is passed to it before the rejection
 *     propagates — matching the R08 documented contract
 *     that the host sees auth/network failures via a
 *     callback even when it does not await the init
 *     promise directly (e.g. the UMD auto-init path).
 *
 * The pipeline returns a ``WidgetRuntime`` shape carrying
 * the resolved config, the backend version, and a handle
 * for future surface mounts. Batch C surfaces consume this
 * runtime; Batch B ships the orchestration and the types.
 */

import { WidgetApiClient, type OnAuthRefresh } from '../api/client';
import { WidgetError } from '../api/errors';
import { fetchAnonSession } from './anon-session';
import {
  fetchWidgetConfig,
  type WidgetConfigResponse,
  type WidgetSurface,
} from './config';
import { renderErrorState } from './error-state';
import { assertSurfaceEnabled } from './surfaces';
import { applyTheme } from './theme';
import {
  performVersionHandshake,
  type WidgetBackendVersion,
} from './version';

export interface RunInitPipelineOptions {
  /** Galen deployment base URL (no trailing slash required). */
  baseUrl: string;
  /**
   * Host-minted JWT; sent as Bearer on every backend call.
   *
   * WIDGET-V1-R26: optional. When omitted (and the deployment opts in
   * via ``WIDGET_ANONYMOUS_ACCESS_ENABLED``), the widget bootstraps an
   * anonymous session from ``POST /api/widget/anon-session`` and runs
   * with that short-lived ``anonymous``-role token instead — and
   * re-mints it transparently on 401 / expiry. If the deployment has
   * not opted in, omitting ``token`` surfaces an init error.
   */
  token?: string;
  /** Widget SDK version, compared against the backend range. */
  widgetVersion: string;
  /** Backend semver range the widget was built for. */
  compatibleBackendRange: string;
  /**
   * DOM element that hosts the widget; receives the
   * theme's ``--galen-*`` CSS custom properties.
   */
  container: HTMLElement;
  /**
   * Optional surface the caller wants to render. When
   * supplied the init pipeline asserts it is in the
   * deployment's ``enabled_surfaces`` list before
   * returning successfully. Omit to only initialise the
   * runtime and let the caller mount surfaces later.
   */
  surface?: string;
  /**
   * R08 callback used to re-mint the JWT on 401. Wired
   * through to ``WidgetApiClient`` so every backend call
   * inherits the refresh-and-retry behaviour.
   */
  onAuthRefresh?: OnAuthRefresh;
  /**
   * Optional callback invoked with any non-recoverable
   * error before the init promise rejects. Gives host
   * pages a fire-and-forget error channel that does not
   * require awaiting the init promise.
   */
  onError?: (error: Error) => void;
}

export interface WidgetRuntime {
  client: WidgetApiClient;
  config: WidgetConfigResponse;
  backend: WidgetBackendVersion;
  container: HTMLElement;
}

/**
 * Execute the full widget init flow. See the module-level
 * docstring for the four-phase sequence.
 */
export async function runInitPipeline(
  options: RunInitPipelineOptions,
): Promise<WidgetRuntime> {
  try {
    // 0. WIDGET-V1-R26: anonymous bootstrap. If the host gave no JWT,
    //    mint a short-lived anonymous-role session token from the
    //    deployment's public mint endpoint and use it as the Bearer;
    //    re-mints on 401/expiry replace the host-supplied refresh
    //    callback (an anonymous widget refreshes itself). If the
    //    deployment has not opted in the mint endpoint 404s and this
    //    throws a clear init error.
    const runningAnonymously = !options.token;
    const initialToken = runningAnonymously
      ? await fetchAnonSession(options.baseUrl)
      : (options.token as string);
    const onAuthRefresh: OnAuthRefresh | undefined = runningAnonymously
      ? () => fetchAnonSession(options.baseUrl)
      : options.onAuthRefresh;

    const client = new WidgetApiClient({
      baseUrl: options.baseUrl,
      token: initialToken,
      onAuthRefresh,
    });

    // 1. Version handshake — if the backend is incompatible
    //    the widget cannot safely proceed. Clinicians must
    //    never see widget output against a mismatched backend.
    const backend = await performVersionHandshake(
      client,
      options.widgetVersion,
      options.compatibleBackendRange,
    );

    // 2. Config fetch — authenticated; the R08 retry layer
    //    inside WidgetApiClient transparently refreshes
    //    the JWT on 401 so this call sees a single logical
    //    success or a surfaced WidgetAuthError.
    const config = await fetchWidgetConfig(client);

    // 3. Theme application — writes CSS custom properties
    //    to the container so all surfaces rendered into it
    //    inherit the configured tokens.
    applyTheme(options.container, config.config.theme);

    // 4. Surface allowlist check (only when a surface was
    //    requested). Second-level defence: Batch C surface
    //    wrappers ALSO call the same gate at mount time.
    if (options.surface !== undefined) {
      assertSurfaceEnabled(
        options.surface,
        config.config.enabled_surfaces as readonly WidgetSurface[],
      );
    }

    return { client, config, backend, container: options.container };
  } catch (e) {
    const err = e instanceof Error ? e : new WidgetError(String(e));

    // WIDGET-V1-R20: before the promise rejects, render the
    // documented error card into the widget container so a
    // clinician sees a user-visible failure state instead
    // of a blank region. The renderer is idempotent —
    // re-entering after a failed re-init replaces the
    // previous card in-place.
    try {
      renderErrorState(options.container, err);
    } catch (renderErr) {
      // eslint-disable-next-line no-console
      console.error(
        '[GalenWidget] renderErrorState threw while handling an init failure:',
        renderErr,
      );
    }

    if (options.onError) {
      // onError is advisory — any exception it throws must
      // not mask the original init failure, so run it
      // inside a try block and log the onError mishap but
      // still propagate the underlying error.
      try {
        options.onError(err);
      } catch (callbackErr) {
        // eslint-disable-next-line no-console
        console.error(
          '[GalenWidget] onError callback threw:',
          callbackErr,
        );
      }
    }
    throw err;
  }
}
